import { parseDocument } from '../services/api';

export const IMPORT_ARTIFACT_TYPES = {
  CONTEXT: 'context',
  PROMPT: 'prompt',
  SKILL: 'skill',
  AGENT: 'agent',
  SUPPORTING: 'supporting',
  DESCRIPTION: 'description',
  SKIP: 'skip',
};

export function isPromptLikeImportArtifactType(artifactType) {
  return (
    artifactType === IMPORT_ARTIFACT_TYPES.PROMPT ||
    artifactType === IMPORT_ARTIFACT_TYPES.SKILL ||
    artifactType === IMPORT_ARTIFACT_TYPES.AGENT
  );
}

export function isContextLikeImportArtifactType(artifactType) {
  return (
    artifactType === IMPORT_ARTIFACT_TYPES.CONTEXT ||
    artifactType === IMPORT_ARTIFACT_TYPES.SUPPORTING
  );
}

export function capabilityTypeForImportArtifact(artifactType) {
  if (artifactType === IMPORT_ARTIFACT_TYPES.SKILL) return 'skill';
  if (artifactType === IMPORT_ARTIFACT_TYPES.AGENT) return 'agent';
  return 'prompt';
}

export function importConflictArtifactType(artifactType) {
  if (isPromptLikeImportArtifactType(artifactType)) return IMPORT_ARTIFACT_TYPES.PROMPT;
  if (isContextLikeImportArtifactType(artifactType)) return IMPORT_ARTIFACT_TYPES.CONTEXT;
  return artifactType;
}

export const IMPORT_CONFLICT_RESOLUTION = {
  UPDATE: 'update',
  SKIP: 'skip',
  CREATE: 'create',
};

const conflictResolutionLabels = {
  [IMPORT_CONFLICT_RESOLUTION.UPDATE]: 'Update existing',
  [IMPORT_CONFLICT_RESOLUTION.SKIP]: 'Skip',
  [IMPORT_CONFLICT_RESOLUTION.CREATE]: 'Create new',
};

export function getImportConflictResolutionLabel(resolution) {
  return conflictResolutionLabels[resolution] || resolution;
}

export const RAW_CONTEXT_IMPORT_EXTENSIONS = new Set([
  'js', 'jsx', 'ts', 'tsx', 'py', 'sh', 'bash', 'zsh', 'json', 'jsonl', 'yaml', 'yml',
  'toml', 'xml', 'html', 'htm', 'css', 'scss', 'sass', 'env', 'ini', 'conf', 'config',
  'mjs', 'cjs', 'java', 'go', 'rs', 'rb', 'php', 'cs', 'cpp', 'c', 'h', 'hpp', 'sql',
  'graphql', 'gql', 'dockerfile', 'gitignore', 'dockerignore',
]);

export const SUPPORTED_IMPORT_EXTENSIONS = new Set([
  'docx', 'pdf', 'txt', 'md', 'markdown', 'html', 'htm', 'csv', 'json',
  ...RAW_CONTEXT_IMPORT_EXTENSIONS,
]);

export const SUPPORTED_IMPORT_LABEL = 'DOCX, PDF, TXT, Markdown, HTML, CSV, JSON, YAML, and common code/config files';

const PROMPT_NAME_HINTS = ['prompt', 'instruction', 'instructions', 'capability', 'skill', 'agent'];
const DESCRIPTION_NAME_HINTS = ['readme', 'description', 'about', 'overview', 'info', 'information'];

export function getImportFileExtension(fileName = '') {
  return String(fileName).split('.').pop()?.toLowerCase() || '';
}

export function stripImportFileExtension(fileName = '') {
  return String(fileName).replace(/\.[^.]+$/, '') || 'Untitled';
}

export function normalizeImportPath(path = '') {
  return String(path || '').replace(/\\/g, '/').replace(/^\/+|\/+$/g, '');
}

export function getImportFolderPath(relativePath = '') {
  const normalized = normalizeImportPath(relativePath);
  const parts = normalized.split('/').filter(Boolean);
  parts.pop();
  return parts.join('/');
}

export function isSupportedImportFile(file) {
  const ext = getImportFileExtension(file?.name);
  return SUPPORTED_IMPORT_EXTENSIONS.has(ext);
}

export function shouldImportAsRawContext(fileName = '') {
  const ext = getImportFileExtension(fileName);
  return RAW_CONTEXT_IMPORT_EXTENSIONS.has(ext) || ['txt', 'md', 'markdown', 'csv'].includes(ext);
}

export function inferImportArtifactType(file) {
  if (!isSupportedImportFile(file)) return IMPORT_ARTIFACT_TYPES.SKIP;

  const baseName = stripImportFileExtension(file?.name || '').toLowerCase();
  if (DESCRIPTION_NAME_HINTS.some((hint) => baseName === hint || baseName.includes(hint))) {
    return IMPORT_ARTIFACT_TYPES.DESCRIPTION;
  }
  if (PROMPT_NAME_HINTS.some((hint) => baseName === hint || baseName.includes(hint))) {
    return IMPORT_ARTIFACT_TYPES.PROMPT;
  }
  return IMPORT_ARTIFACT_TYPES.CONTEXT;
}

function normalizeImportName(name = '') {
  return stripImportFileExtension(name).trim().toLowerCase();
}

/** Match import file base name to an existing project prompt or context (case-insensitive). */
export function findImportNameConflict(baseName, artifactType, prompts = [], contexts = []) {
  const key = normalizeImportName(baseName);
  if (!key) return null;
  if (isPromptLikeImportArtifactType(artifactType)) {
    const match = (prompts || []).find(
      (p) => normalizeImportName(p?.title || p?.name || '') === key
    );
    if (match?.id) {
      return { type: 'prompt', existingId: match.id, existingName: match.title || match.name || key };
    }
  }
  if (isContextLikeImportArtifactType(artifactType)) {
    const match = (contexts || []).find(
      (c) => normalizeImportName(c?.name || '') === key
    );
    if (match?.id) {
      return { type: 'context', existingId: match.id, existingName: match.name || key };
    }
  }
  return null;
}

export function attachImportConflicts(records, prompts = [], contexts = []) {
  return (records || []).map((record) => {
    if (!record.supported) return { ...record, conflict: null };
    if (
      !isPromptLikeImportArtifactType(record.artifactType) &&
      !isContextLikeImportArtifactType(record.artifactType)
    ) {
      return { ...record, conflict: null };
    }
    const match = findImportNameConflict(record.name, record.artifactType, prompts, contexts);
    if (!match) return { ...record, conflict: null };
    return {
      ...record,
      conflict: {
        ...match,
        resolution: IMPORT_CONFLICT_RESOLUTION.SKIP,
      },
    };
  });
}

export function buildImportRecords(fileList) {
  return Array.from(fileList || [])
    .filter((file) => {
      if (!file) return false;
      if (!file.name.startsWith('.')) return true;
      return SUPPORTED_IMPORT_EXTENSIONS.has(getImportFileExtension(file.name));
    })
    .map((file, index) => {
      const relativePath = normalizeImportPath(file.webkitRelativePath || file.name);
      const supported = isSupportedImportFile(file);
      return {
        id: `${index}-${relativePath || file.name}`,
        file,
        name: file.name,
        relativePath,
        folderPath: getImportFolderPath(relativePath),
        extension: getImportFileExtension(file.name),
        supported,
        artifactType: supported ? inferImportArtifactType(file) : IMPORT_ARTIFACT_TYPES.SKIP,
        category: getImportFolderPath(relativePath) || 'General',
        description: `Imported from ${relativePath || file.name}`,
        tags: buildImportTags({ folderPath: getImportFolderPath(relativePath), relativePath }),
        status: supported ? 'ready' : 'skipped',
        parsedText: '',
        stats: null,
        error: supported ? '' : `Unsupported file type. Supported: ${SUPPORTED_IMPORT_LABEL}`,
      };
    })
    .sort((a, b) => a.relativePath.localeCompare(b.relativePath));
}

export function buildImportTags(record) {
  const tags = ['imported'];
  if (record.folderPath) tags.push(`folder:${record.folderPath}`);
  if (record.relativePath) tags.push(`source:${record.relativePath}`);
  return tags;
}

export function buildDescriptionAppendBlock(record, markdown) {
  return [
    `## Imported: ${record.relativePath || record.name}`,
    '',
    markdown || '',
  ].join('\n');
}

export function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Read file text for import/upload flows: raw text locally or POST /documents/parse for binary docs.
 * Shared by project import, knowledge builder, prompt builder, and temp context.
 */
export async function readImportFileText(file, fileName) {
  if (!file) return '';
  const name = fileName || file.name || '';
  if (shouldImportAsRawContext(name)) {
    return file.text();
  }
  const fileContent = await readFileAsDataUrl(file);
  const extension = getImportFileExtension(name);
  const parsed = await parseDocument(fileContent, name, file.type || extension);
  return parsed?.text || '';
}

function attachRelativePath(file, relativePath) {
  if (!file || file.webkitRelativePath) return file;
  try {
    Object.defineProperty(file, 'webkitRelativePath', {
      value: relativePath,
      configurable: true,
    });
  } catch {
    // Some environments may not allow redefining; fall back to the bare file.
  }
  return file;
}

async function readDirectoryEntries(reader) {
  const entries = [];
  let batch = await new Promise((resolve, reject) => {
    reader.readEntries(resolve, reject);
  });
  while (batch.length > 0) {
    entries.push(...batch);
    batch = await new Promise((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
  }
  return entries;
}

async function walkFileTreeEntry(entry, files, pathPrefix = '') {
  if (!entry) return;
  if (entry.isFile) {
    const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
    const relativePath = pathPrefix ? `${pathPrefix}/${file.name}` : file.name;
    files.push(attachRelativePath(file, relativePath));
    return;
  }
  if (!entry.isDirectory) return;
  const reader = entry.createReader();
  const childEntries = await readDirectoryEntries(reader);
  const nextPrefix = pathPrefix ? `${pathPrefix}/${entry.name}` : entry.name;
  await Promise.all(childEntries.map((child) => walkFileTreeEntry(child, files, nextPrefix)));
}

/** Collect dropped files/folders from a DataTransfer (directory-aware when supported). */
export async function collectFilesFromDataTransfer(dataTransfer) {
  const items = dataTransfer?.items;
  if (items?.length) {
    const entries = [];
    for (let i = 0; i < items.length; i += 1) {
      const entry = items[i].webkitGetAsEntry?.();
      if (entry) entries.push(entry);
    }
    if (entries.length > 0) {
      const files = [];
      await Promise.all(entries.map((entry) => walkFileTreeEntry(entry, files)));
      if (files.length > 0) return files;
    }
  }
  return Array.from(dataTransfer?.files || []);
}
